import type { ReactNode } from 'react'
import { Link } from 'react-router'

import { cn } from '@/shared/ui'
import { inlineLink, targetSize } from '@/shared/ui/cn'

import { useAccess } from './access-context'
import type { PermissionId } from './permissions'
import { useRecordReach } from './record-reach'

/**
 * Whether the caller may open the record page `to`, as {@link RecordLink} decides it: `false` until known.
 */
export function useMayOpenRecord(to: string, permission: PermissionId, scope?: string | readonly string[]): boolean {
  const access = useAccess()
  // A grant with no scope, or at all organizations, reaches every record, whatever `scope` asks.
  const held = access.can(permission, scope) || access.reachesEverything(permission)
  const everywhere = held && access.reachesEverything(permission)
  // Asked only of a caller whose reach is partial.
  const reach = useRecordReach(held && !everywhere ? to : null)
  return held && (everywhere || reach === null || reach === true)
}

/**
 * Another record's name: a link to its page for a caller who may open it, plain text for anyone
 * else and until that is known. They may when they hold `permission` (the record's read
 * permission) at a scope that reaches every record, or at any scope when no feature registered
 * which of those records they reach (`RecordReachProvider`); otherwise only when it names this one.
 * With `scope`, only a grant at that scope (or one of several) counts.
 */
export function RecordLink(props: {
  readonly to: string
  readonly permission: PermissionId
  readonly scope?: string | readonly string[] | undefined
  readonly children: ReactNode
  readonly className?: string
}) {
  return useMayOpenRecord(props.to, props.permission, props.scope) ? (
    <Link to={props.to} className={cn(inlineLink, targetSize, props.className)}>
      {props.children}
    </Link>
  ) : (
    props.children
  )
}
